// fillVariant.js — Classify a tactical-area SIDC into a fill rendering variant.
//
// Three variants match the 08-area-fill.md spec:
//   'hatch'           — 45° diagonal hatch + centroid glyph  (CBRN types)
//   'perimeter-stamp' — polygon outline + perimeter stamp walk (minefields, encirclement)
//   'solid'           — transparent solid fill (everything else)
//
// Matching strategy:
//   SIDC positions are 0-indexed. Position 1 is the standard-identity digit (varies
//   freely: 3=friend, 6=hostile, etc.) — we never match on it. We match on the
//   function-code characters at fixed positions that uniquely identify each family.
//
//   MIL-STD-2525D 20-char SIDCs have the same function code layout in positions 2–9
//   as the 15-char 2525C masks used in the spec tables, so both formats work.

/** @typedef {'hatch'|'perimeter-stamp'|'solid'} FillVariant */
/** @typedef {'mushroom'|'inverted-t'|'teardrop'|'trefoil'|null} CentroidGlyph */

// Each entry: [substring to test, starting character index]
// We extract sidc.slice(2, 9) (7 chars) which covers the function-code block
// and compare against fixed suffixes, keeping the match independent of
// standard-identity (position 1).

const HATCH_MATCHES = [
    // Characters 2-8 of a 2525D/C SIDC for each CBRN type
    // G?MPNZN  → Nuclear
    // G?MPNZC  → Chemical
    // G?MPNZB  → Biological
    // G?MPNZR  → Radiological
    'MPNZN',
    'MPNZC',
    'MPNZB',
    'MPNZR',
];

const PERIMETER_STAMP_MATCHES = [
    // G?MSPMCF → Confirmed Minefield
    // G?MSPMBB → Mine Belt
    // G?MSPMMX → Mixed Minefield
    // G?GPGAEN → Encirclement
    'MSPMCF',
    'MSPMBB',
    'MSPMMX',
    'GPGAEN',
];

/** @type {Map<string, CentroidGlyph>} */
const GLYPH_MAP = new Map([
    ['MPNZN', 'mushroom'],
    ['MPNZC', 'inverted-t'],
    ['MPNZB', 'teardrop'],
    ['MPNZR', 'trefoil'],
]);

/**
 * Extract the 5–7 char function-code window from a SIDC string.
 * Handles both 2525C (15-char) and 2525D (20-char) SIDCs.
 * Returns the slice starting at position 2 (after scheme + standard-identity).
 *
 * @param {string} sidc
 * @returns {string}
 */
function fnCode(sidc) {
    if (!sidc || typeof sidc !== 'string' || sidc.length < 7) return '';
    // Normalise to upper-case; wildcard chars ('?', '*') are in non-matched positions
    return sidc.slice(2).toUpperCase();
}

/**
 * Classify a tactical-area SIDC into a fill rendering variant.
 *
 * @param {string|null|undefined} sidc
 * @returns {FillVariant}
 */
export function getFillVariant(sidc) {
    const fn = fnCode(sidc);
    if (!fn) return 'solid';

    for (const pattern of HATCH_MATCHES) {
        if (fn.startsWith(pattern)) return 'hatch';
    }
    for (const pattern of PERIMETER_STAMP_MATCHES) {
        if (fn.startsWith(pattern)) return 'perimeter-stamp';
    }
    return 'solid';
}

/**
 * Return the centroid glyph key for CBRN (Variant B) areas, or null.
 *
 * @param {string|null|undefined} sidc
 * @returns {CentroidGlyph}
 */
export function getCentroidGlyph(sidc) {
    const fn = fnCode(sidc);
    if (!fn) return null;
    for (const [pattern, glyph] of GLYPH_MAP) {
        if (fn.startsWith(pattern)) return glyph;
    }
    return null;
}
